import {
  PROTOCOL_VERSION,
  type OnlineAuthenticatedRequest,
  type OnlineCredentials,
  type OnlineJoinRequest,
  type OnlineJoinResponse,
  type OnlineRoomResponse,
  type OnlineRoomSnapshot,
  type OnlineSubmitRequest,
  type ServerEnvelope,
  type ServerErrorEnvelope,
} from "../../shared/protocol/index.js";

export const ONLINE_CREDENTIALS_STORAGE_KEY = "yoake-online-credentials-v1";

export class OnlineApiError extends Error {
  public constructor(public readonly code: string, message: string, public readonly status: number) {
    super(message);
  }
}

export class OnlineApiClient {
  public constructor(private readonly baseUrl = "") {}

  public async join(request: OnlineJoinRequest): Promise<OnlineJoinResponse> {
    return this.post<OnlineJoinResponse>("/api/online/join", request);
  }

  public async getState(credentials: OnlineCredentials): Promise<OnlineRoomSnapshot> {
    const response = await this.post<OnlineRoomResponse>("/api/online/state", credentials);
    return response.room;
  }

  public async submit(request: OnlineSubmitRequest): Promise<OnlineRoomSnapshot> {
    return (await this.post<OnlineRoomResponse>("/api/online/submit", request)).room;
  }

  public async leave(credentials: OnlineCredentials): Promise<void> {
    await this.post<{ left: true }>("/api/online/leave", credentials);
  }

  private post<T>(path: string, body: OnlineJoinRequest | OnlineAuthenticatedRequest | OnlineSubmitRequest): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const response = await fetch(this.baseUrl + path, {
      ...init,
      cache: "no-store",
    });
    const body = await response.json() as ServerEnvelope<T> | ServerErrorEnvelope;
    if (!response.ok || "error" in body) {
      const error = "error" in body
        ? body.error
        : { code: "request_failed", message: "サーバーへ接続できませんでした。" };
      throw new OnlineApiError(error.code, error.message, response.status);
    }
    if (body.protocolVersion !== PROTOCOL_VERSION) {
      throw new OnlineApiError("protocol_mismatch", "ゲームとサーバーの版が一致しません。", 409);
    }
    return body.payload;
  }
}

export function saveOnlineCredentials(credentials: OnlineCredentials): void {
  window.sessionStorage.setItem(ONLINE_CREDENTIALS_STORAGE_KEY, JSON.stringify(credentials));
}

export function loadOnlineCredentials(): OnlineCredentials | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(ONLINE_CREDENTIALS_STORAGE_KEY) ?? "null") as Partial<OnlineCredentials> | null;
    if (!value || typeof value.roomId !== "string" || typeof value.token !== "string") return null;
    if (value.playerIndex !== 0 && value.playerIndex !== 1) return null;
    return { roomId: value.roomId, token: value.token, playerIndex: value.playerIndex };
  } catch {
    return null;
  }
}

export function clearOnlineCredentials(): void {
  window.sessionStorage.removeItem(ONLINE_CREDENTIALS_STORAGE_KEY);
}
